// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `viewport-layer-overrides` route body (PR-C, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `viewport-layer-overrides` nested drill-down: the per-VIEWPORT layer-PROPERTY-override RAW
// `{code, handle}` pairs (DXF groups 335 / 343 / 344 / 91), `?viewportEntityId` REQUIRED. The seam
// already enforced the requirement (absent → `missing-viewport-id`) and the existence (non-VIEWPORT id →
// `unknown-viewport-id`). The body FILTERS `snapshot.viewports` to that ONE viewport and projects its
// `layerOverrideHandles[]` VERBATIM — each item is `{ code, handle }` RAW.
//
// FULL CONTRACT SUPPORT, NOT a placeholder (ПРИОРИТЕТ #1): the contract (fullstack:965) wants the items
// RAW `{code, handle}` — NOT decoded. `ViewportEntity.layerOverrideHandles` (`{code:int, handle:string}[]`,
// model.ts:1526, shipped #291) is EXACTLY this typed object-graph; the route returns it verbatim. The
// SEMANTIC resolution (handle → override VALUE + per-VP render) is the SEPARATELY-deferred RESOLVE slice the
// contract ITSELF defers — returning the raw handles is the complete contract for THIS route (round-trip-
// preserve), never a stub.
//
// routeRevisions: `{ geometryRevision, tableRevision }` — same reasoning as `viewport-frozen-layers`: a
// per-VP override is ViewportEntity entity-state (geometryRevision) referencing the layer table
// (tableRevision). `snapshotRevision` rides as the page ANCHOR only (INV-9).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers. depcruise cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import { type RouteProjector } from "../sceneSummaryProjector";
import { compareFacetsBy, finishNestedViewportPage, parseKeyDirSort } from "./projectionHelpers";
import { findTargetViewport } from "./projectViewportFrozenLayers";

/** The LEAN CORE (and only) field set: the RAW `{ code, handle }` pair. */
const LEAN_CORE_FIELDS = ["code", "handle"] as const;

/** The legal `key:dir` sort KEYS (by the RAW group code, or the handle). */
const OVERRIDE_SORT_KEYS = ["code", "handle"] as const;
type OverrideSortKey = (typeof OVERRIDE_SORT_KEYS)[number];

/** One projected override row: the RAW `{ code, handle }` pair (verbatim — NO decode). */
interface OverrideRow {
  readonly code: number;
  readonly handle: string;
}

/** Extract an override row's sort value: `code` is the RAW int group code; `handle` is the RAW string. */
function overrideSortValue(row: OverrideRow, key: OverrideSortKey): string | number | undefined {
  switch (key) {
    case "code":
      return row.code;
    case "handle":
      return row.handle;
    default:
      return undefined;
  }
}

/**
 * The `viewport-layer-overrides` route body. resolve target VP → project its RAW
 * `layerOverrideHandles[]` `{code,handle}` pairs VERBATIM → sort → (fullField | field-project) →
 * paginate. Binds `routeRevisions` to `{ geometryRevision, tableRevision }`. `snapshotRevision` rides
 * as the page ANCHOR only. `resolvedSpaceId: null` (a nested drill-down is keyed by the viewport).
 * byteTruncated: false. NO decode — the contract returns raw handles (FULL support; the RESOLVE slice
 * is separately deferred).
 */
export const projectViewportLayerOverrides: RouteProjector = ({ query, cursor, snapshot }) => {
  // RESOLVE the ONE viewport `?viewportEntityId` names (the seam validated it resolves). A defensive
  // miss → an empty overrides list (empty page). Project the RAW `{code, handle}` pairs VERBATIM.
  // `layerOverrideHandles` is OPTIONAL on the schema (model.ts:1526 — absent for an override-free
  // viewport) → `?? []` so such a viewport returns an empty page (total 0) instead of throwing.
  const target = findTargetViewport(snapshot.viewports, query.viewportEntityId);
  const rows: OverrideRow[] =
    target === undefined
      ? []
      : (target.layerOverrideHandles ?? []).map((o) => ({ code: o.code, handle: o.handle }));

  // SORT (before pagination — cursor-determinism). No `?sort` → the RAW list order (round-trip
  // preserved). A `code` tie (the same group code appears for multiple layers) breaks by `handle` ASC
  // so a continuation pages deterministically across an equal-code run.
  const parsedSort = parseKeyDirSort<OverrideSortKey>(query.sort, OVERRIDE_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? rows
      : [...rows].sort((a, b) =>
          compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, overrideSortValue, "handle"),
        );

  // PROJECTION-MODE + PAGINATION + FIELD-PROJECTION + ENVELOPE — the SHARED NESTED-route tail. RAW — NO
  // decode (the `{code,handle}` pairs are projected verbatim). Both nested viewport routes bind
  // `{ geometryRevision, tableRevision }` + `resolvedSpaceId: null` (a per-VP override is ViewportEntity
  // entity-state referencing the layer table, fullstack:965; keyed by the viewport, not a space).
  // `snapshotRevision` rides as the page ANCHOR only.
  return finishNestedViewportPage(ordered, query, cursor, LEAN_CORE_FIELDS, snapshot);
};
